import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, productsAPI } from '@/lib/api';
import { formatCurrency, formatDate, getPaymentStatusColor } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Car, 
  FileText, 
  AlertTriangle,
  Plus,
  ArrowRight,
  Package
} from 'lucide-react';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, lowStockRes] = await Promise.all([
          dashboardAPI.getStats(),
          productsAPI.getLowStock()
        ]);
        setStats(statsRes.data);
        setLowStockProducts(lowStockRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black font-heading tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Welcome back! Here's your garage overview for today.
          </p>
        </div>
        <Link to="/invoices/new">
          <Button className="font-semibold uppercase tracking-wide btn-active" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card card-hover" data-testid="today-revenue-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Today's Revenue</p>
                <p className="text-2xl font-bold font-heading mt-1">
                  {formatCurrency(stats?.today?.revenue || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.today?.invoice_count || 0} invoices
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card accent-green card-hover" data-testid="today-profit-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Today's Profit</p>
                <p className="text-2xl font-bold font-heading mt-1">
                  {formatCurrency(stats?.today?.profit || 0)}
                </p>
                <p className="text-xs text-green-600 mt-1">Net earnings</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover" data-testid="customers-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Customers</p>
                <p className="text-2xl font-bold font-heading mt-1">
                  {stats?.totals?.customers || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.totals?.vehicles || 0} vehicles
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card accent-yellow card-hover" data-testid="pending-payments-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Payments</p>
                <p className="text-2xl font-bold font-heading mt-1">
                  {formatCurrency(stats?.totals?.pending_amount || 0)}
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  {stats?.totals?.pending_payments || 0} invoices
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Invoices */}
        <div className="lg:col-span-2">
          <Card data-testid="recent-invoices-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Recent Invoices</CardTitle>
              <Link to="/invoices">
                <Button variant="ghost" size="sm" className="text-primary">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_invoices?.length > 0 ? (
                <div className="space-y-4">
                  {stats.recent_invoices.map((invoice) => (
                    <Link
                      key={invoice.id}
                      to={`/invoices/${invoice.id}`}
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                      data-testid={`invoice-item-${invoice.invoice_no}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-mono font-medium text-sm">{invoice.invoice_no}</p>
                          <p className="text-sm text-muted-foreground">{invoice.customer_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(invoice.grand_total)}</p>
                        <Badge className={getPaymentStatusColor(invoice.payment_status)}>
                          {invoice.payment_status}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground/50" />
                  <p className="mt-2">No invoices yet</p>
                  <Link to="/invoices/new">
                    <Button variant="outline" className="mt-4">Create First Invoice</Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Low Stock Alerts */}
        <div>
          <Card data-testid="low-stock-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Low Stock Alerts
              </CardTitle>
              <Badge variant="destructive">{lowStockProducts.length}</Badge>
            </CardHeader>
            <CardContent>
              {lowStockProducts.length > 0 ? (
                <div className="space-y-3">
                  {lowStockProducts.slice(0, 5).map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-100"
                      data-testid={`low-stock-item-${product.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Package className="w-5 h-5 text-red-500" />
                        <div>
                          <p className="font-medium text-sm">{product.name}</p>
                          {product.sku && (
                            <p className="text-xs text-muted-foreground font-mono">{product.sku}</p>
                          )}
                        </div>
                      </div>
                      <Badge variant="destructive">{product.stock} left</Badge>
                    </div>
                  ))}
                  {lowStockProducts.length > 5 && (
                    <Link to="/products">
                      <Button variant="outline" className="w-full mt-2">
                        View All ({lowStockProducts.length})
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto opacity-50" />
                  <p className="mt-2">All items in stock</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="font-heading">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <Link to="/customers/new">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-add-customer-btn">
                  <Users className="w-5 h-5" />
                  <span className="text-xs">Add Customer</span>
                </Button>
              </Link>
              <Link to="/vehicles/new">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-add-vehicle-btn">
                  <Car className="w-5 h-5" />
                  <span className="text-xs">Add Vehicle</span>
                </Button>
              </Link>
              <Link to="/products/new">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-add-product-btn">
                  <Package className="w-5 h-5" />
                  <span className="text-xs">Add Part</span>
                </Button>
              </Link>
              <Link to="/reports/daily">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-reports-btn">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-xs">Reports</span>
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
